import { useState } from 'react';
import {
  makeStyles,
  tokens,
  Card,
  CardHeader,
  Text,
  Body1,
  Body2,
  Caption1,
  Spinner,
  MessageBar,
  MessageBarBody,
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import { DocumentArrowDownRegular, ArrowSyncRegular, ArrowDownloadRegular, ClockRegular } from '@fluentui/react-icons';
import { MaturityScoreGauge } from './MaturityScoreGauge';
import { PillarCard } from './PillarCard';
import { TopGapsList } from './TopGapsList';
import { RuntimeBlindSpotsCard } from './RuntimeBlindSpotsCard';
import { QualificationGateBanner } from './QualificationGateBanner';
import {
  useDiagnosticSummary,
  useRuntimeBlindSpotsSummary,
  useRecalculateBlindSpots,
  useGenerateReport,
  useQualificationStatus,
  useDiagnosticTrend,
  useDiagnosticSchedule,
  useExportDiagnostic,
} from '../hooks';
import { getMaturityLevel, PILLAR_COLORS } from '../utils/scoring';
import { UnauthenticatedBanner } from '../../../components/common/UnauthenticatedBanner';
import type { DiagnosticPillarKey, DiagnosticExportFormat } from '../types';

interface DiagnosticDashboardProps {
  onViewBlindSpots: () => void;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
  },
  topRow: {
    display: 'grid',
    gridTemplateColumns: '280px 1fr',
    gap: tokens.spacingHorizontalXL,
    alignItems: 'start',
  },
  gaugeCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
  },
  pillarsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  bottomRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actionButtons: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  reportBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  scheduleInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    justifyContent: 'flex-end',
  },
});

/** The four pillar keys in display order. */
const PILLAR_KEYS: DiagnosticPillarKey[] = [
  'controlCoverage',
  'enforcementGaps',
  'evidenceIntegrity',
  'runtimeBlindSpots',
];

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return 'unknown';
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m ago`;
  return `${minutes}m ago`;
}

function formatTimeUntil(dateStr: string | null): string {
  if (!dateStr) return 'unknown';
  const diff = new Date(dateStr).getTime() - Date.now();
  if (diff <= 0) return 'any moment';
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function DiagnosticDashboard({ onViewBlindSpots }: DiagnosticDashboardProps) {
  const styles = useStyles();
  const { data: summary, isLoading: summaryLoading, error: summaryError } = useDiagnosticSummary();
  const { data: blindSpotsSummary, isLoading: bsLoading, error: bsError } = useRuntimeBlindSpotsSummary();
  const { data: qualification, isLoading: qualLoading } = useQualificationStatus();
  const recalculate = useRecalculateBlindSpots();
  const { generateReport, isGenerating, reportData } = useGenerateReport();
  const { data: trend } = useDiagnosticTrend();
  const { data: schedule } = useDiagnosticSchedule();
  const { exportData, isExporting } = useExportDiagnostic();
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<DiagnosticExportFormat>('json');

  const isQualified = qualification?.isQualified ?? true;

  if (summaryLoading || bsLoading || qualLoading) {
    return <Spinner label="Loading diagnostic data..." />;
  }

  if (summaryError || bsError) {
    const err = (summaryError || bsError) as any;
    const isUnauth = err?.status === 401 || err?.message?.includes('MSAL');
    if (isUnauth) {
      return <UnauthenticatedBanner feature="diagnostic data" />;
    }
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          Failed to load diagnostic data: {(err as Error).message}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (!summary || !blindSpotsSummary) {
    return (
      <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
        Run your first diagnostic assessment to calculate governance maturity scores across control coverage, enforcement gaps, evidence integrity, and runtime blind spots.
      </Body1>
    );
  }

  const maturityLevel = getMaturityLevel(summary.overallScore);

  return (
    <div className={styles.container}>
      {/* Qualification gate banner */}
      {qualification && !isQualified && (
        <QualificationGateBanner qualification={qualification} />
      )}

      {/* Action bar */}
      <div className={styles.headerRow}>
        <div />
        <div className={styles.actionButtons}>
          <Button
            appearance="outline"
            icon={<ArrowDownloadRegular />}
            onClick={() => setExportDialogOpen(true)}
            disabled={!isQualified}
          >
            Export
          </Button>
          <Button
            appearance="outline"
            icon={<ArrowSyncRegular />}
            onClick={() => recalculate.mutate()}
            disabled={!isQualified || recalculate.isPending}
          >
            {recalculate.isPending ? 'Recalculating...' : 'Recalculate'}
          </Button>
          <Button
            appearance="primary"
            icon={<DocumentArrowDownRegular />}
            onClick={() => generateReport()}
            disabled={!isQualified || isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate Report'}
          </Button>
        </div>
      </div>

      {/* Report status banner */}
      {reportData && (
        <MessageBar intent={reportData.downloadUrl ? 'success' : 'info'}>
          <MessageBarBody>
            <div className={styles.reportBanner}>
              <Text>{reportData.message}</Text>
              {reportData.downloadUrl && (
                <Button
                  appearance="primary"
                  size="small"
                  onClick={() => window.open(reportData.downloadUrl!, '_blank')}
                >
                  Download Report
                </Button>
              )}
            </div>
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Top row: Maturity gauge + 4 pillar cards */}
      <div className={styles.topRow}>
        <Card className={styles.gaugeCard}>
          <CardHeader
            header={<Text weight="semibold">Maturity Score</Text>}
            description={<Body2>Governance readiness</Body2>}
          />
          <MaturityScoreGauge
            score={summary.overallScore}
            level={maturityLevel}
          />
        </Card>
        <div className={styles.pillarsGrid}>
          {PILLAR_KEYS.map((key) => (
            <PillarCard
              key={key}
              pillarKey={key}
              score={summary.pillars[key]}
              accentColor={PILLAR_COLORS[key]}
              trend={trend?.delta?.[key]}
            />
          ))}
        </div>
      </div>

      {/* Bottom row: Top gaps + Runtime blind spots summary */}
      <div className={styles.bottomRow}>
        <TopGapsList gaps={summary.topGaps} />
        <RuntimeBlindSpotsCard summary={blindSpotsSummary} onViewAll={onViewBlindSpots} />
      </div>

      {/* Calculated timestamp + schedule info */}
      <div>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3, textAlign: 'right', display: 'block' }}>
          Last calculated: {new Date(summary.calculatedAt).toLocaleString()}
        </Text>
        {schedule && (
          <div className={styles.scheduleInfo}>
            <ClockRegular style={{ color: tokens.colorNeutralForeground3, fontSize: '12px' }} />
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              {schedule.lastRunUtc
                ? `Last scan ${formatRelativeTime(schedule.lastRunUtc)}`
                : 'No scan completed yet'}
              {schedule.nextRunUtc && ` · Next scan in ${formatTimeUntil(schedule.nextRunUtc)}`}
            </Caption1>
          </div>
        )}
      </div>

      {/* Export dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={(_, data) => setExportDialogOpen(data.open)}>
        <DialogSurface>
          <DialogTitle>Export Diagnostic Data</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              <Text>Export diagnostic summary, blind spots, and historical snapshots (last 30 days).</Text>
              <Dropdown
                value={exportFormat.toUpperCase()}
                onOptionSelect={(_, data) => setExportFormat(data.optionValue as DiagnosticExportFormat)}
              >
                <Option value="json">JSON — Machine-readable format</Option>
                <Option value="csv">CSV — Spreadsheet format</Option>
              </Dropdown>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
            <Button
              appearance="primary"
              icon={<ArrowDownloadRegular />}
              onClick={() => {
                exportData(exportFormat);
                setExportDialogOpen(false);
              }}
              disabled={isExporting}
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
